import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

/**
 * Wrap modalRef so that dismissal (via backdrop click, ESC key, or dismiss())
 * never causes an unhandled promise rejection that pauses browser debuggers.
 */
export function makeSafeModalRef(modalRef: NgbModalRef): NgbModalRef {
  // 1. Mark the root result promise as handled so it never triggers window.onunhandledrejection
  modalRef.result.catch(() => {});

  // 2. Wrap .then so that downstream calls like `modalRef.result.then(result => ...)` without
  //    an onRejected callback do not create unhandled promise rejections on dismiss.
  const originalThen = modalRef.result.then.bind(modalRef.result);
  modalRef.result.then = function (onfulfilled?: any, onrejected?: any) {
    const safeOnRejected = onrejected ?? (() => {});
    return originalThen(onfulfilled, safeOnRejected);
  } as any;

  // 3. Wrap .finally so that downstream calls like `modalRef.result.finally(() => ...)`
  //    do not return an unhandled rejected promise on dismiss.
  const originalFinally = modalRef.result.finally.bind(modalRef.result);
  modalRef.result.finally = function (onfinally?: any) {
    const finPromise = originalFinally(onfinally);
    finPromise.catch(() => {});
    return finPromise;
  } as any;

  return modalRef;
}

@Injectable({ providedIn: 'root' })
export class SafeNgbModal extends NgbModal {
  override open(content: any, options?: NgbModalOptions): NgbModalRef {
    const modalRef = super.open(content, options);
    return makeSafeModalRef(modalRef);
  }
}
